import "./CheckoutPage.scss";

import Cookies from "js-cookie";
import moment from "moment-business-days-sk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Stripe from "stripe";

import AddressManager from "@components/AddressManager";
import HsaFsaPaymentFlow from "@components/HsaFsaPaymentFlow";
import OverlayPanel from "@components/OverlayPanel";
import PaymentMethodManager from "@components/PaymentMethodManager";
import Header from "@elements/Header";
import useApplePay from "@hooks/billing/useApplePay";
import useCreatePaymentMethod from "@hooks/billing/useCreatePaymentMethod";
import useLinkExpressCheckout from "@hooks/billing/useLinkExpressCheckout";
import useValidatePaymentForm from "@hooks/billing/useValidatePaymentMethod";
import useValidatePromo from "@hooks/billing/useValidatePromo";
import useCase from "@hooks/case/useCase";
import useRemoveMedication from "@hooks/case/useRemoveMedication";
import useRestoreMedication from "@hooks/case/useRestoreMedication";
import useShipping from "@hooks/order/useShipping";
import useDataLayer from "@hooks/useDataLayer";
import { useTitle } from "@hooks/useTitle";
import Loader from "@img/Loader.svg";
import {
  AlertBanner,
  AlertBannerMessage,
  Button,
  Card,
  Feedback,
  FormGroupMessage,
  Link,
  Option,
  Text,
  TextField,
  Title,
} from "@runwayhealth/runway-components-react";
import * as casesActions from "@store/cases/casesActions";
import * as casesSlice from "@store/cases/casesSlice";
import { REQUEST_CASE_PRICE } from "@store/prices/pricesActions";
import { removedMedicationsCleared } from "@store/removedMedications/removedMedicationsSlice";
import {
  ADD_PAYMENT_METHOD,
  GET_AFFILIATE_PROFILE,
  GET_PROMO_CODES,
} from "@store/user/userActions";
import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { GA4Events } from "@types-runway/analytics";
import {
  PaymentFormField,
  PaymentGateway,
  PaymentMethodForm,
  PaymentMethodManagerView,
} from "@types-runway/billing";
import { Case, CaseUpdateInput, ShippingMethods } from "@types-runway/case";
import { Condition } from "@types-runway/condition";
import { ErrorEvents, PricesState, RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";
import { mapConditionPrescriptions } from "@utils/case/conditionPrescriptions";
// Visit Price ID (Stripe reference for visit fee.)
import {
  OVERNIGHT_SHIPPING_ID,
  PRIORITY_SHIPPING_ID,
  VISIT_FEE_STRIPE_PRICE_ID,
} from "@utils/constantsEnv";
import { toastError, toastInfo } from "@utils/utils";

import { coupons } from "../../../api";
import { MedicationSummaryRows } from "./Components/MedicationSummaryRows";
import { resolvePaymentMethodsForPayCase } from "./resolvePaymentMethodsForPayCase";
import { ShippingBody, ShippingContainer } from "./ShippingMethod";

const CheckoutPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  // Context entities.
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);
  const newCase = useSelector<RootState, RootEntity<Case>>((state) => state.case.newCase);
  const userCases = useSelector<RootState, RootEntity<Case>[]>((state) => state.case.cases.data);
  // Services prices.
  const prices = useSelector<RootState, PricesState>((state) => state.price);
  const paymentMethodForm = useSelector<RootState, PaymentMethodForm>(
    (state) => state.paymentMethodForm
  );
  //Promo codes & discounts.
  const [promo, setPromo] = useState({
    discount: 0,
    havePromo: false,
    code: "",
    id: "",
  });
  // Checkout summary.
  const totalMedicationPrice = useMemo(
    () =>
      (newCase.data?.conditions || []).reduce((sum, condition) => sum + (condition?.price || 0), 0),
    [newCase]
  );

  const [shippingPriceAmount, setShippingPriceAmount] = useState(0);

  const [isReferred, setIsReferred] = useState(false);

  const [pendingAddressConfirmation, setPendingAddressConfirmation] = useState(false);

  const [promoCodeInputIsOpen, setPromoCodeInputIsOpen] = useState(false);

  const [disabledButton, setDisabledButton] = useState(false);

  const [addressManagerIsOpen, setAddressManagerIsOpen] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [shippingPrices, setShippingPrices] = useState<Stripe.ShippingRate[]>([]);

  const { reportPurchasedProducts } = useDataLayer(GA4Events.BEGIN_CHECKOUT);

  const { updateCase, fetchCaseDetails } = useCase();

  const { removeMedication } = useRemoveMedication();
  const { restoreMedication, removedMedications } = useRestoreMedication();

  const { fetchShippingPrices } = useShipping();

  const { validatePromo, isValidPromo, promoDetails } = useValidatePromo();

  const [fsaPaymentMethodIsOpen, setFsaPaymentMethodIsOpen] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway | null>(null);

  const stripe = useStripe();
  const elements = useElements();

  // Guards against the card-form submit button and the independent Apple Pay/
  // Link "paymentmethod"/"confirm" events all triggering payCase() for the
  // same case — a ref (not state) so the check is synchronous and closes the
  // race regardless of how long the saga takes to flip newCase.isPending.
  // Shared across all three flows, so it lives here rather than inside any
  // one payment-method hook.
  const paymentInFlightRef = useRef(false);
  // A failed medication removal or restore is reported inline by MedicationSummaryRows,
  // so its error must not also raise the page-level toast (it would be announced twice).
  const isMedicationChangeRequestedRef = useRef(false);
  const isPaymentInFlight = () => paymentInFlightRef.current;
  const setPaymentInFlight = (value: boolean) => {
    paymentInFlightRef.current = value;
  };

  const canRemoveMedication = !newCase.data.invoice && !newCase.data.paid;
  const isRemovalBlocked = newCase.isPending || fsaPaymentMethodIsOpen || disabledButton;
  const handleRemoveMedication = (productName: string) => {
    if (isPaymentInFlight()) return;

    isMedicationChangeRequestedRef.current = true;
    removeMedication(productName);
  };
  const handleRestoreMedication = (productName: string) => {
    if (isPaymentInFlight()) return;

    isMedicationChangeRequestedRef.current = true;
    restoreMedication(productName);
  };

  const totalDueNow = prices.case.data.unit_amount - promo.discount;
  const { paymentRequest, canUseApplePay } = useApplePay(stripe, totalDueNow, {
    isPaymentInFlight,
    setPaymentInFlight,
    onPaymentMethodAttached: () => payCase({ manual: true, justAttachedPaymentMethod: true }),
  });

  const {
    canUseLink,
    handleReady: handleLinkReady,
    handleConfirm: handleLinkConfirm,
  } = useLinkExpressCheckout(stripe, elements, {
    isPaymentInFlight,
    validateBeforeConfirm: () => validateAddressConfirmation(),
    setPaymentInFlight,
    onPaymentMethodAttached: () => payCase({ manual: true, justAttachedPaymentMethod: true }),
  });

  // Memoize the HsaFsaPaymentFlow component to prevent remounting on re-renders
  // This is critical to avoid reloading the payment iframe mid-transaction
  const HsaFsaPaymentComponent = useCallback(() => {
    return (
      <HsaFsaPaymentFlow
        caseId={newCase.data.id!}
        onClose={() => setFsaPaymentMethodIsOpen(false)}
        promoCodeId={promo.id}
        totalAmount={prices.case.data.unit_amount - promo.discount}
      />
    );
  }, [newCase.data.id, promo.id, prices.case.data.unit_amount, promo.discount]);

  const addDaysAndFormat = (date: Date, days: number) => {
    const dateWithDays = moment(date).businessAdd(days).toDate();
    return dateWithDays.toLocaleString("en-us", {
      month: "short",
      day: "numeric",
    });
  };

  // Payment Method Hook
  const { createPaymentMethod, mapStripePaymentMethodToDTO } = useCreatePaymentMethod();

  // Validation form hook.
  const { validateForm } = useValidatePaymentForm();

  // Delivery dates
  const today = new Date();
  const superfast = addDaysAndFormat(today, 2);
  const fastest = addDaysAndFormat(today, 3);
  const slowest = addDaysAndFormat(today, 8);
  const neutral = addDaysAndFormat(today, 5);

  const [errorText, setErrorText] = useState("");

  const isCaseAssignedToUser = (caseData: Case): boolean => {
    return caseData.UserId !== null && caseData.UserId !== undefined && caseData.UserId !== "";
  };

  const checkoutIsReady = useMemo(() => {
    const paymentFormErrorState = Object.keys(paymentMethodForm)
      .filter((field) => field)
      .map((key) => paymentMethodForm[key as PaymentFormField].error?.flagged);

    const paymentFormIsComplete = Object.keys(paymentMethodForm)
      .filter((field) => field !== undefined)
      .map((key) => paymentMethodForm[key as PaymentFormField].value !== "");

    return (
      (!paymentFormErrorState.includes(true) && !paymentFormIsComplete.includes(false)) ||
      user.data.paymentMethods?.length > 0
    );
  }, [newCase.data.shippingAddress, paymentMethodForm, user.data.paymentMethods]);

  useTitle("Checkout");

  // Hook effects.

  // Find offers before component mounts.
  useMemo(() => {
    // Get referral link cookie.
    const referralCookie = JSON.parse(Cookies.get("rewardful.referral") ?? "{}");

    // Find offers
    if (referralCookie.coupon) {
      // Fetchs affiliate profile.
      if (!user.data.affiliateProfile) {
        dispatch({
          type: GET_AFFILIATE_PROFILE,
        });
      }
    } else {
      // Find rewards.
      dispatch({
        type: GET_PROMO_CODES,
      });
      // Find discount cookie
      let discountCookie: string | null = null;
      discountCookie = Cookies.get("promo-code") ?? null;
      if (discountCookie) {
        validatePromo(discountCookie);
      }
    }
    // Collect analytics
    reportPurchasedProducts();
  }, []);

  // Load visit fee price
  useEffect(() => {
    dispatch({
      type: REQUEST_CASE_PRICE,
      priceId: VISIT_FEE_STRIPE_PRICE_ID,
    });
  }, []);

  // Send case if paid
  useEffect(() => {
    if (newCase.data.paid) {
      dispatch(removedMedicationsCleared({ caseId: newCase.data.id as number }));
      sendCase();
    }

    if (newCase.data?.invoice?.paid) {
      payCase({ manual: false });
    }
  }, [newCase.data.paid, newCase.data?.invoice?.paid]);

  // Checks errors
  useEffect(() => {
    const errorMessage = newCase?.error?.message || "";
    const showErrorMessage =
      !errorMessage.includes("Unknown") &&
      !errorMessage.toLocaleLowerCase().includes("sequelize") &&
      errorMessage.length > 0;
    if (showErrorMessage) {
      if (
        errorMessage !== ErrorEvents.PENDING_INVOICE &&
        errorMessage !== ErrorEvents.FAILED_INVOICE &&
        // addPaymentMethod (userSaga.ts) already toasted this failure.
        errorMessage !== ErrorEvents.ADD_PAYMENT_METHOD
      ) {
        // A genuine failure (not "we're already handling it") means nothing
        // was actually submitted — safe to let the user retry.
        paymentInFlightRef.current = false;
        if (!isMedicationChangeRequestedRef.current) toastError("Error.", errorMessage);
      }

      if (errorMessage === ErrorEvents.FAILED_INVOICE) {
        toastInfo(
          "We’re already on it.",
          "We ran into a small issue while processing your payment, but our team is taking care of it. You don’t need to do anything else."
        );
      }
    }
  }, [newCase.error]);

  // Must stay after the error effect: it runs first when a failed save ends.
  useEffect(() => {
    if (!newCase.isPending) isMedicationChangeRequestedRef.current = false;
  }, [newCase.isPending]);

  // ADD_PAYMENT_METHOD failures surface on user.error, not newCase.error —
  // without this, a failure here (with no matching newCase error) would leave
  // paymentInFlightRef stuck true, permanently blocking further submit attempts.
  useEffect(() => {
    if (user.error?.message) {
      paymentInFlightRef.current = false;
    }
  }, [user.error]);

  // Validate consultation
  useEffect(() => {
    const caseId = newCase.data.id ?? 0;

    if (isCaseAssignedToUser(newCase.data) && caseId) {
      (async () => {
        setLoadingSummary(true);

        // Map conditions.
        let mappedConditions: Condition[] = [];

        const caseDetails = await fetchCaseDetails(caseId);

        if (caseDetails && caseDetails.conditions.length > 0) {
          mappedConditions = await mapConditionPrescriptions(
            caseDetails.conditions,
            caseDetails.trip?.days
          );
        }

        const caseUpdateBody: CaseUpdateInput = {
          conditions: mappedConditions,
        };

        updateCase(caseId, Boolean(newCase.data.UserId), caseUpdateBody);
        setLoadingSummary(false);
      })();
    }
  }, [newCase.data.UserId, newCase.data.id]);

  // Update user ID if it is not set
  useEffect(() => {
    const caseId = newCase.data.id ?? 0;

    if (user.data.id && caseId && !isCaseAssignedToUser(newCase.data)) {
      const caseUpdateBody: CaseUpdateInput = {
        UserId: user.data.id,
      };

      updateCase(caseId, false, caseUpdateBody);
    }
  }, [user.data.id]);

  // Update shipping prices on shipping address change
  useEffect(() => {
    (async () => {
      const pricesResult = await fetchShippingPrices([
        PRIORITY_SHIPPING_ID ?? "",
        OVERNIGHT_SHIPPING_ID ?? "",
      ]);
      setShippingPrices(pricesResult);
    })();
  }, [user.data.addresses]);

  useEffect(() => {
    if (!isValidPromo) {
      setErrorText("The code is invalid. Please enter a valid code.");
      return;
    } else {
      const discountAmount =
        promoDetails?.coupon.amount_off ??
        prices.case.data.unit_amount * ((promoDetails?.coupon.percent_off ?? 0) / 100);

      setPromo({
        code: promoDetails?.code ?? "",
        id: promoDetails?.id ?? "",
        discount: discountAmount,
        havePromo: true,
      });
      setErrorText("");

      // Save promocode in local storage for analytics purposes.
      if (promoDetails?.code) {
        localStorage.setItem("promoCode", promoDetails?.code);
      }
    }
  }, [isValidPromo, promoDetails]);

  // Validates referrals.
  useEffect(() => {
    if (user.data.affiliateProfile) {
      // Get referral link cookie.
      const referralCookie = JSON.parse(Cookies.get("rewardful.referral") ?? "{}");

      // If there is a referral cookie.
      if (referralCookie.coupon) {
        // Validate referral.
        let referralIsValid = false;
        // If referral token is equals to the cookie token, then is invalid.
        if (referralCookie.affiliate?.token !== user.data.affiliateProfile.links[0].token) {
          // Valid referral has no previous purchases.
          const userPaidCases = userCases.filter((case_: RootEntity<Case>) => case_.data.caseId);
          if (userPaidCases.length === 0) {
            referralIsValid = true;
          }
        }

        if (!referralIsValid) {
          // Remove the cookie.
          Cookies.remove("rewardful.referral");
          return;
        }
        coupons.couponGet(`/?id=${referralCookie.coupon.id}`).then((coupon) => {
          const discountAmount = prices.case.data.unit_amount * (coupon.data.percent_off / 100);
          setPromo((prev) => ({
            ...prev,
            discount: discountAmount,
            havePromo: true,
          }));
          setIsReferred(true);
        });
      }
    }
  }, [user.data.affiliateProfile]);

  useEffect(() => {
    const promocodes = user.data?.promocodes;
    if (promocodes && promocodes.length > 0) {
      // Find active promocodes for user.
      const activePromocodes = user.data.promocodes
        .filter((promocode) => promocode.active === true)
        .map((promocodeObject) => promocodeObject.code);

      if (activePromocodes.length > 0) {
        const code = activePromocodes[0];
        // Redundanly apply promo code to avoid
        // async state transition
        setPromo((prev) => ({ ...prev, code, havePromo: true }));
        validatePromo(code);
      }
    }
  }, [user.data.promocodes, promo]);

  const setShipping = (shipping: ShippingMethods | null) => {
    dispatch(casesSlice.updateShippingMethod(shipping));
    if (newCase.data.id) {
      const caseUpdateBody: CaseUpdateInput = {
        shipping_method: shipping,
      };

      updateCase(newCase.data.id, true, caseUpdateBody);
    }
  };

  const removePromoCode = async () => {
    setPromo((prev) => ({ ...prev, id: "", code: "", discount: 0, havePromo: false }));
  };

  const handleCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPromo((prev) => ({ ...prev, code: value }));
  };

  const processCase = async () => {
    if (paymentInFlightRef.current) {
      return;
    }

    const addressConfirmed = validateAddressConfirmation();

    if (!addressConfirmed) {
      return;
    }

    paymentInFlightRef.current = true;
    setDisabledButton(true);
    if (user.data.paymentMethods.length === 0) {
      const validForm = validateForm(paymentMethodForm);

      if (!validForm) {
        paymentInFlightRef.current = false;
        setDisabledButton(false);
        return;
      }
      // Add the payment method first.
      const paymentMethodResult = await createPaymentMethod(
        paymentMethodForm,
        stripe,
        elements,
        CardNumberElement
      );

      if (paymentMethodResult?.paymentMethod) {
        const newPaymentMethod = mapStripePaymentMethodToDTO(paymentMethodResult.paymentMethod);
        dispatch({
          type: ADD_PAYMENT_METHOD,
          paymentMethodId: newPaymentMethod?.id,
          view: PaymentMethodManagerView.CHECKOUT,
        });
      }
    }
    // Manual payment; matches the same wait-for-attach guarantee Apple Pay/Link get.
    payCase({ manual: true, justAttachedPaymentMethod: user.data.paymentMethods.length === 0 });

    setDisabledButton(false);
  };

  const validateAddressConfirmation = () => {
    if (addressManagerIsOpen) {
      // Highlight address confirmation button.
      // User hint
      // Scroll to top to highlight address confirmation
      window.scrollTo({ top: 200, behavior: "smooth" });
      setPendingAddressConfirmation(true);
      return false;
    }
    return true;
  };

  const payCase = async ({
    manual,
    justAttachedPaymentMethod = false,
  }: {
    manual: boolean;
    justAttachedPaymentMethod?: boolean;
  }) => {
    dispatch({
      type: casesActions.PAY_CASE,
      newCaseId: newCase.data.id,
      paymentMethods: resolvePaymentMethodsForPayCase(
        user.data.paymentMethods,
        justAttachedPaymentMethod
      ),
      promoCodeId: promo.id,
      customerId: user.data.customer_id,
      manual,
    });
  };

  const sendCase = async () => {
    const isPlus = sessionStorage.getItem("flow") === "plus" || false;
    dispatch({
      type: casesActions.SEND_CASE,
      newCase: newCase.data,
      isPlus,
    });
  };

  const formattedPrice = (value: number, decimals = 0) => (value / 100).toFixed(decimals);

  const setCaseShippingAddress = async (addressId: string) => {
    const caseUpdateBody: CaseUpdateInput = {
      shippingAddress: {
        id: addressId,
      },
    };

    if (newCase.data.id && newCase.data.UserId) {
      updateCase(newCase.data.id, true, caseUpdateBody);
    }

    setAddressManagerIsOpen(false);
    setPendingAddressConfirmation(false);
  };

  return (
    <>
      <Header hasProgressBar={false} isCheckout previous={() => history.push("/consultation")} />
      {/*  Flex container */}
      <div className="checkout-container">
        {/* Left flex column */}
        <div className="left-checkout-column">
          {/* Shipping container */}
          <div>
            <Card className="checkout-card">
              <>
                {addressManagerIsOpen ? (
                  <AddressManager
                    user={user}
                    openForm={!user.data.addresses || user.data.addresses.length === 0}
                    confirmSelectedAddress={setCaseShippingAddress}
                    newCase={newCase}
                    closeManager={() => {
                      setAddressManagerIsOpen(false);
                      setPendingAddressConfirmation(false);
                    }}
                    pendingAddressConfirmation={pendingAddressConfirmation}
                    isCheckout
                  />
                ) : (
                  <>
                    <div className="ph-no-capture shipping-address-container">
                      <div>
                        <Title as="h2" size="md-bold">
                          Shipping Address
                        </Title>
                        <Button
                          size="sm"
                          variant="outline"
                          iconName="Pencil"
                          iconPosition="left"
                          onClick={() => setAddressManagerIsOpen(true)}
                        >
                          Edit
                        </Button>
                      </div>
                      {newCase.isPending ? (
                        <>
                          {" "}
                          <img className="loader-img" src={Loader} />
                        </>
                      ) : (
                        <div className="shipping-address">
                          <Text>
                            {user.data.first_name} {user.data.last_name},
                          </Text>
                          <Text>
                            {newCase.data.shippingAddress?.street},{" "}
                            {newCase.data.shippingAddress?.city},{" "}
                            {newCase.data.shippingAddress?.state}
                          </Text>
                          <Text>{newCase.data.shippingAddress?.postalCode}</Text>
                        </div>
                      )}
                    </div>
                    <div className="medicine-fee">
                      <Title as="h2" size="md-bold">
                        Shipping Method
                      </Title>
                      {newCase.data.conditions.length === 0 && (
                        <p>In case you get a prescription, please choose a shipping method:</p>
                      )}
                      <ShippingContainer>
                        <Option
                          id="standard"
                          checked={newCase.data.shipping_method === null}
                          value="standard"
                          onClick={() => {
                            setShipping(null);
                            setShippingPriceAmount(0);
                          }}
                        >
                          <ShippingBody>
                            <div className="shipping-body-info">
                              <div>
                                <Text as="span" size="sm" noMarginBottom>
                                  Estimated arrival between
                                </Text>
                                <Title as="h3" size="sm" noMarginBottom>
                                  {`${neutral} - ${slowest}`}
                                </Title>
                              </div>
                              <div>
                                <Title as="h3" size="tiny" noMarginBottom>
                                  Standard
                                </Title>
                                <Text as="span" size="sm" noMarginBottom>
                                  5-8 business days
                                </Text>
                              </div>
                            </div>
                            <div className="shipping-body-price">
                              <Title as="h3" size="md-bold" className="free-price" noMarginBottom>
                                FREE
                              </Title>
                            </div>
                          </ShippingBody>
                        </Option>

                        <Option
                          id="priority"
                          checked={newCase.data.shipping_method === ShippingMethods.USPS_PRIORITY}
                          value="priority"
                          onClick={() => {
                            setShipping(ShippingMethods.USPS_PRIORITY);
                            setShippingPriceAmount(shippingPrices[0]?.fixed_amount?.amount ?? 0);
                          }}
                        >
                          <ShippingBody>
                            <div className="shipping-body-info">
                              <div>
                                <Text as="span" size="sm" noMarginBottom>
                                  Estimated arrival between
                                </Text>
                                <Title as="h3" size="sm" noMarginBottom>
                                  {`${fastest} - ${neutral}`}
                                </Title>
                              </div>
                              <div>
                                <Title as="h3" size="tiny" noMarginBottom>
                                  Priority
                                </Title>
                                <Text as="span" size="sm" noMarginBottom>
                                  3-5 business days
                                </Text>
                              </div>
                            </div>
                            <div className="shipping-body-price">
                              <Title
                                data-testid="shipping-price-1"
                                as="h3"
                                size="md-bold"
                                noMarginBottom
                              >
                                $
                                {shippingPrices.length > 0
                                  ? formattedPrice(shippingPrices[0]?.fixed_amount?.amount || 0, 2)
                                  : 0}
                              </Title>
                            </div>
                          </ShippingBody>
                        </Option>
                        <Option
                          id="express"
                          checked={
                            newCase.data.shipping_method === ShippingMethods.FEDX_STANDARD_OVERNIGHT
                          }
                          value="express"
                          onClick={() => {
                            setShipping(ShippingMethods.FEDX_STANDARD_OVERNIGHT);
                            setShippingPriceAmount(shippingPrices[1]?.fixed_amount?.amount ?? 0);
                          }}
                        >
                          <ShippingBody>
                            <div className="shipping-body-info">
                              <div>
                                <Text as="span" size="sm" noMarginBottom>
                                  Estimated arrival between
                                </Text>
                                <Title as="h3" size="sm" noMarginBottom>
                                  {`${superfast} - ${fastest}`}
                                </Title>
                              </div>
                              <div>
                                <Title as="h3" size="tiny" noMarginBottom>
                                  Express
                                </Title>
                                <Text as="span" size="sm" noMarginBottom>
                                  2-3 business days
                                </Text>
                              </div>
                            </div>
                            <div className="shipping-body-price">
                              <Title
                                data-testid="shipping-price-2"
                                as="h3"
                                size="md-bold"
                                noMarginBottom
                              >
                                $
                                {shippingPrices.length > 0
                                  ? formattedPrice(shippingPrices[1]?.fixed_amount?.amount || 0, 2)
                                  : 0}
                              </Title>
                            </div>
                          </ShippingBody>
                        </Option>
                      </ShippingContainer>
                    </div>
                  </>
                )}
              </>
            </Card>
          </div>
          {/* Payment details container */}
          <div>
            <div>
              <>
                <PaymentMethodManager
                  caseId={newCase.data.id}
                  stripeHook={stripe}
                  stripeElementsHook={elements}
                  user={user}
                  view={PaymentMethodManagerView.CHECKOUT}
                  onGatewayChange={setSelectedGateway}
                  paymentRequest={paymentRequest}
                  canUseApplePay={canUseApplePay}
                  onApplePayButtonClick={validateAddressConfirmation}
                  canUseLink={canUseLink}
                  onLinkReady={handleLinkReady}
                  onLinkConfirm={handleLinkConfirm}
                  isProcessingPayment={newCase.isPending}
                />
              </>
            </div>
          </div>
        </div>
        {/* Right flex column */}
        <div className="right-checkout-column">
          {/* Order summary container */}
          <div>
            <Card className="checkout-card">
              <div>
                <Title as="h2" size="md-bold">
                  Due now
                </Title>
                {/* Consultation fee */}
                {!user.isPending ? (
                  <>
                    <div className="highlighted-fee">
                      <Title as="h3" size="sm">
                        Consultation Fee
                      </Title>
                      <Title as="span" size="sm">
                        {`$${formattedPrice(prices.case.data.unit_amount - promo.discount)}`}
                      </Title>
                    </div>
                    {promo.discount > 0 && (
                      <Title className="crossed-out-visit-fee" as="h3" size="sm">
                        $30
                      </Title>
                    )}
                    {isReferred && (
                      <div className="banner-wrapper">
                        {" "}
                        <AlertBanner variant="success" isRounded>
                          <AlertBannerMessage>Referral discount applied.</AlertBannerMessage>
                        </AlertBanner>
                      </div>
                    )}
                  </>
                ) : (
                  // Consultation Fee Loader
                  <>
                    {" "}
                    <img className="loader-img" src={Loader} />
                  </>
                )}
                {/* Treatment summary */}
                <hr></hr>
                <div>
                  {newCase.data.conditions.length > 0 && (
                    <>
                      {" "}
                      <Title as="h2" size="md-bold">
                        Due only if prescribed
                      </Title>
                      <div className="highlighted-fee">
                        <Title as="h3" size="sm">
                          Treatment Total
                        </Title>
                        <Title as="h3" size="sm">
                          ${formattedPrice(totalMedicationPrice)}
                        </Title>
                      </div>
                    </>
                  )}
                  <MedicationSummaryRows
                    conditions={newCase.data.conditions}
                    isLoadingSummary={loadingSummary}
                    canRemove={canRemoveMedication}
                    isBusy={isRemovalBlocked}
                    onRemove={handleRemoveMedication}
                    removedMedications={removedMedications}
                    onRestore={handleRestoreMedication}
                  />
                  <div className="shipping-fee">
                    <Title as="h3" size="sm">
                      Shipping Fee
                    </Title>
                    <Title as="h3" size="sm">
                      ${formattedPrice(shippingPriceAmount)}
                    </Title>
                  </div>
                </div>
                {/* Order Total */}
                <hr></hr>
                <div className="order-total-wrapper">
                  <Title as="h2" size="md-bold">
                    Order total
                  </Title>
                  <Title as="h2" size="md-bold">
                    $
                    {formattedPrice(
                      totalMedicationPrice +
                        shippingPriceAmount +
                        prices.case.data.unit_amount -
                        promo.discount
                    )}
                  </Title>
                </div>
              </div>
              {/* Promocode Link */}
              <div className="promocode-link-wrapper">
                {!isReferred &&
                  promo.discount === 0 &&
                  !user.data?.partnerTx &&
                  !promoCodeInputIsOpen && (
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPromoCodeInputIsOpen(true);
                      }}
                    >
                      Have a discount code?
                    </Link>
                  )}
              </div>
              {/* Promocode Section Title */}
              {((promo.havePromo && promo.id) || promoCodeInputIsOpen) && (
                <Title as="h2" size="md-bold">
                  Have a discount code?
                </Title>
              )}
              {/* Promocode Input */}
              <div>
                {promoCodeInputIsOpen && !promo.havePromo && (
                  <>
                    {" "}
                    <div className="promocode-input-wrapper">
                      <TextField
                        placeholder="Discount code"
                        onChange={handleCode}
                        isInvalid={errorText !== ""}
                      />
                      <FormGroupMessage>
                        <Feedback size="sm" isInvalid={errorText !== ""}>
                          {errorText}
                        </Feedback>
                      </FormGroupMessage>
                      <Link
                        href="#"
                        onClick={(e) => {
                          if (!promo.code.length) setErrorText("Please enter your discount code");
                          e.preventDefault();
                          validatePromo(promo.code);
                        }}
                      >
                        Apply code
                      </Link>
                    </div>
                  </>
                )}
              </div>
              {/* Applied promocode */}
              {promo.havePromo && promo.id && (
                <div>
                  <div className="promocode-input-wrapper">
                    <TextField
                      placeholder="Discount code"
                      onChange={handleCode}
                      value={promo.code}
                      disabled
                    />
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault;
                        removePromoCode();
                      }}
                    >
                      Remove code
                    </Link>
                  </div>
                  <div className="banner-wrapper">
                    {" "}
                    <AlertBanner variant="success" isRounded>
                      <AlertBannerMessage>
                        Your discount code has been applied successfully.
                      </AlertBannerMessage>
                    </AlertBanner>
                  </div>
                </div>
              )}
              <div className="pay-button-container">
                <div className="clinician-message-hint">
                  <p>
                    A licensed physician will review your submission after checkout. Any questions?
                    You can message your physician directly from your Runway Dashboard.
                  </p>
                </div>
                {pendingAddressConfirmation && (
                  <div className="checkout-form-error-notice">
                    <p>Please confirm your shipping address.</p>
                  </div>
                )}
                <Button
                  size="lg"
                  isLoading={newCase.isPending}
                  iconName={selectedGateway === PaymentGateway.HSA_FSA ? "CheckCircle" : ""}
                  disabled={
                    selectedGateway === null ||
                    user.isPending ||
                    newCase.isPending ||
                    (selectedGateway === PaymentGateway.CREDIT_CARD &&
                      (!checkoutIsReady || disabledButton)) ||
                    (selectedGateway === PaymentGateway.HSA_FSA &&
                      (!newCase.data.id || newCase.data.shippingAddress === null))
                  }
                  onClick={() => {
                    if (selectedGateway === PaymentGateway.HSA_FSA) {
                      const addressConfirmed = validateAddressConfirmation();
                      if (!addressConfirmed) {
                        return;
                      }
                      setFsaPaymentMethodIsOpen(true);
                    } else {
                      processCase();
                    }
                  }}
                  customWidth={400}
                  iconPosition="left"
                >
                  {selectedGateway === PaymentGateway.HSA_FSA
                    ? "Pay with HSA/FSA"
                    : "Complete Payment"}
                </Button>
                <div className="payment-agreement-notice">
                  <p>
                    By clicking &quot;
                    {selectedGateway === PaymentGateway.HSA_FSA
                      ? "Pay with HSA/FSA"
                      : "Complete Payment"}
                    &quot; you agree that:
                  </p>
                  <p>
                    If prescribed, your{" "}
                    {selectedGateway === PaymentGateway.HSA_FSA ? "HSA/FSA funds" : "credit card"}{" "}
                    will be charged for the amount indicated in your treatment total and shipping
                    fee.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <OverlayPanel
        open={fsaPaymentMethodIsOpen}
        close={() => setFsaPaymentMethodIsOpen(false)}
        fullScreen={true}
        child={HsaFsaPaymentComponent}
      />
    </>
  );
};

export default CheckoutPage;
